// src/realtime-admin.ts
import type { Store } from "vuex";
import type { Component } from "vue";
import type { PluginOptions } from "vue-toastification";
import type { ToastInterface } from "vue-toastification";

// Si tu utilises socket.io côté admin :
import { socket } from "@/realtime"; // <- réutilise ton client existant

type InboxStore = {
  handleMessageIngested: (msg: any) => Promise<void> | void;
};

let _wired = false; // évite double initialisation (HMR/navigations)

export function isAdminContext(): boolean {
  try {
    return /^\/admin(\/|$)/.test(location.pathname);
  } catch {
    return false;
  }
}

function ensureNotificationsModule(store: Store<any>) {
  const path = ["notifications"] as any;

  // Vuex 4 fournit hasModule(path[]) si tu l'avais dans ton projet
  const has = (store as any).hasModule
    ? (store as any).hasModule(path)
    : false;

  if (!has) {
    (store as any).registerModule(path, {
      namespaced: true,
      state: () => ({ items: [] as any[], lastReadAt: 0 }),
      getters: {
        unreadCount(state: any): number {
          return state.items.filter((n: any) => {
            const at = new Date(n.at || Date.now()).getTime();
            return at > state.lastReadAt;
          }).length;
        },
        lastItems: (state: any) => (n = 10) => state.items.slice(-n).reverse(),
      },
      mutations: {
        PUSH(state: any, n: any) {
          state.items.push(n);
          try {
            localStorage.setItem("notifications:items", JSON.stringify(state.items));
          } catch {}
        },
        MARK_ALL_READ(state: any) {
          state.lastReadAt = Date.now();
          try {
            localStorage.setItem("notifications:lastReadAt", String(state.lastReadAt));
          } catch {}
        },
        HYDRATE(state: any, payload: { items?: any[]; lastReadAt?: number }) {
          if (Array.isArray(payload.items)) state.items = payload.items;
          if (typeof payload.lastReadAt === "number") state.lastReadAt = payload.lastReadAt;
        },
      },
      actions: {
        receivedMessage({ commit }: any, n: any) {
          commit("PUSH", n);
        },
        markAllRead({ commit }: any) {
          commit("MARK_ALL_READ");
        },
        hydrateFromStorage({ commit }: any) {
          try {
            const rawItems = localStorage.getItem("notifications:items") || "[]";
            const rawRead = localStorage.getItem("notifications:lastReadAt") || "0";
            commit("HYDRATE", {
              items: JSON.parse(rawItems),
              lastReadAt: Number(rawRead) || 0,
            });
          } catch {
            commit("HYDRATE", { items: [], lastReadAt: 0 });
          }
        },
      },
    });

    // Hydrate au premier boot
    (store as any).dispatch("notifications/hydrateFromStorage").catch(() => {});
  }
}

type SetupArgs = {
  store: Store<any>;
  inbox: InboxStore;
  toast: ToastInterface;
  CustomToast: Component;
  apiBase: string;
  // Optionnel : personnaliser l’UI du toast sans recréer Toast
  toastOptions?: PluginOptions;
};

export function setupRealtimeForAdmin({
  store,
  inbox,
  toast,
  CustomToast,
  apiBase,
  toastOptions,
}: SetupArgs) {
  if (_wired) return;
  _wired = true;

  // 1) (Optionnel) module notifications pour badge/compteur dans l’UI
  ensureNotificationsModule(store);

  // 2) dédoublonnage (SSE & socket)
  let lastIds = new Set<string>();
  const remember = (id: string | number | undefined | null) => {
    const key = String(id ?? "");
    if (!key) return false;
    if (lastIds.has(key)) return false;
    lastIds.add(key);
    // On garde un sac léger (évite fuite mémoire)
    if (lastIds.size > 200) {
      lastIds = new Set(Array.from(lastIds).slice(-100));
    }
    return true;
  };

  const safeToast = (msg: any) => {
    try {
      toast(
        {
          component: CustomToast,
          props: {
            subject: msg?.subject ?? msg?.sujet ?? "(no subject)",
            preview: msg?.preview ?? msg?.message ?? "",
            at: msg?.at || new Date().toISOString(),
          },
        },
        {
          timeout: 6000,
          hideProgressBar: true,
          closeButton: false,
          icon: false,
          ...(toastOptions || {}),
        }
      );
    } catch (e) {
      console.warn("[realtime-admin] toast error:", (e as any)?.message || e);
    }
  };

  const handleInbound = async (msg: any) => {
    const id =
      msg?.id ||
      msg?.messageId ||
      msg?.message_id ||
      msg?.threadId ||
      msg?.thread_id;

    // Dédupe
    if (id && !remember(id)) return;

    // 2.1 → notifications store (pour badge/center)
    try {
      await (store as any).dispatch("notifications/receivedMessage", msg);
    } catch {}

    // 2.2 → toast visuel
    safeToast(msg);

    // 2.3 → rafraîchir inbox (Pinia)
    try {
      await inbox.handleMessageIngested(msg);
    } catch (e) {
      console.warn("[realtime-admin] inbox.handleMessageIngested error:", e);
    }
  };

  // 3) SSE (source de vérité)
  try {
    const url = `${apiBase.replace(/\/+$/, "")}/api/inbound/sse`;
    const sse = new EventSource(url, { withCredentials: true } as EventSourceInit);

    sse.addEventListener("message.ingested", (ev: MessageEvent) => {
      try {
        handleInbound(JSON.parse(ev.data || "{}"));
      } catch (e) {
        console.warn("[SSE] parse error:", e);
      }
    });

    // fallback “message”
    sse.onmessage = (ev) => {
      try {
        handleInbound(JSON.parse(ev.data || "{}"));
      } catch (e) {
        console.warn("[SSE] parse error:", e);
      }
    };

    sse.onerror = (ev) => {
      // Le navigateur va auto-retry ; on log juste
      console.warn("[SSE] error, will auto-retry:", ev);
    };
  } catch (e) {
    console.warn("[SSE] init error:", (e as any)?.message || e);
  }

  // 4) Socket.IO (appoint)
  try {
    socket.on && socket.on("connect", () => console.info("[realtime] connected (socket.io)"));
    socket.on &&
      socket.on("connect_error", (err: any) =>
        console.warn("[realtime] connect_error:", err?.message || err)
      );
    socket.on && socket.on("message.ingested", (msg: any) => handleInbound(msg));
  } catch (e) {
    console.warn("[realtime] socket init error:", (e as any)?.message || e);
  }
}
